'use client'
import React from 'react'
import { Box, Button } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'

import { MultiCitySegment } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import { TranslationFunction } from '@/src/types/translations'

import { FirstSegmentRow } from './components/FirstSegmentRow'
import { MultiCitySegmentWrapper } from './components/MultiCitySegmentWrapper'

interface DesktopMultiCitySearchFormProps {
    segments: MultiCitySegment[]
    segmentsWithSameAirportError: Set<number>
    travelersState: { adults: number; childs: number; infants: number }
    flightClass: string
    canAddMoreSegment: boolean
    showRemoveSegment: boolean
    onSegmentFieldActivation: (segmentIndex: number, field: 'from' | 'to' | 'date') => void
    onAirportSelect: (segmentIndex: number, value: string, action: 'from' | 'to') => void
    onDateSelect: (segmentIndex: number, dates: any, range: any) => void
    onSwap: (segmentIndex: number) => void
    onAddMore: () => void
    onRemove: (segmentIndex: number) => void
    onSearch: () => void
    getAirportsForSegment: (query: string, segmentIndex: number, field: 'from' | 'to') => void
    isSegmentFieldActive: (segmentIndex: number, field: 'from' | 'to' | 'date') => boolean
    isComponentActive: (component: 'from' | 'to' | 'date' | 'travelers' | null) => boolean
    handleComponentActivation: (component: 'from' | 'to' | 'date' | 'travelers' | null) => void
    closeActiveComponent: () => void
    closeActiveSegmentComponent: () => void
    handleSegment0ClickAway: (event: Event | React.SyntheticEvent) => void
    createSegmentClickAwayHandler: (segmentIndex: number) => (event: Event | React.SyntheticEvent) => void
    getMinDateForSegment: (segmentIndex: number) => Date | null
    handleTravelerChange: (type: 'adults' | 'childs' | 'infants', delta: number) => void
    handleFlightClassChange: (newClass: string) => void
    canIncreaseAdults: boolean
    canIncreaseChildren: boolean
    canIncreaseInfants: boolean
    canDecreaseAdults: boolean
    canDecreaseChildren: boolean
    canDecreaseInfants: boolean
    t: TranslationFunction<'common'>
}

export const DesktopMultiCitySearchForm: React.FC<DesktopMultiCitySearchFormProps> = ({
    segments,
    segmentsWithSameAirportError,
    travelersState,
    flightClass,
    canAddMoreSegment,
    showRemoveSegment,
    onSegmentFieldActivation,
    onAirportSelect,
    onDateSelect,
    onSwap,
    onAddMore,
    onRemove,
    onSearch,
    getAirportsForSegment,
    isSegmentFieldActive,
    isComponentActive,
    handleComponentActivation,
    closeActiveComponent,
    closeActiveSegmentComponent,
        handleSegment0ClickAway,
        createSegmentClickAwayHandler,
        getMinDateForSegment,
        handleTravelerChange,
    handleFlightClassChange,
    canIncreaseAdults,
    canIncreaseChildren,
    canIncreaseInfants,
    canDecreaseAdults,
    canDecreaseChildren,
    canDecreaseInfants,
    t
}) => {
    const firstSegment = segments[0]
    const additionalSegments = segments.slice(1)

    return (
        <Box>
            {/* First Segment - Full row with Travelers and Search */}
            <FirstSegmentRow
                segment={firstSegment}
                segmentIndex={0}
                segmentsWithSameAirportError={segmentsWithSameAirportError}
                travelersState={travelersState}
                flightClass={flightClass}
                isSegmentFieldActive={isSegmentFieldActive}
                isComponentActive={isComponentActive}
                handleComponentActivation={handleComponentActivation}
                onSegmentFieldActivation={onSegmentFieldActivation}
                onAirportSelect={onAirportSelect}
                onDateSelect={onDateSelect}
                onSwap={onSwap}
                onSearch={onSearch}
                getAirportsForSegment={getAirportsForSegment}
                closeActiveComponent={closeActiveComponent}
                closeActiveSegmentComponent={closeActiveSegmentComponent}
                handleSegment0ClickAway={handleSegment0ClickAway}
                handleTravelerChange={handleTravelerChange}
                handleFlightClassChange={handleFlightClassChange}
                canIncreaseAdults={canIncreaseAdults}
                canIncreaseChildren={canIncreaseChildren}
                canIncreaseInfants={canIncreaseInfants}
                canDecreaseAdults={canDecreaseAdults}
                canDecreaseChildren={canDecreaseChildren}
                    canDecreaseInfants={canDecreaseInfants}
                    getMinDateForSegment={getMinDateForSegment}
                    t={t}
                />

            {/* Additional Segments (1, 2, etc.) - From, To, Date only, aligned with first segment */}
            {additionalSegments.map((segment, index) => {
                const segmentIndex = index + 1
                return (
                    <MultiCitySegmentWrapper
                        key={segmentIndex}
                        segment={segment}
                        segmentIndex={segmentIndex}
                        segmentsLength={segments.length}
                        segmentsWithSameAirportError={segmentsWithSameAirportError}
                        isSegmentFieldActive={isSegmentFieldActive}
                        onSegmentFieldActivation={onSegmentFieldActivation}
                        onAirportSelect={onAirportSelect}
                        onDateSelect={onDateSelect}
                        onSwap={onSwap}
                        onRemove={onRemove}
                        getAirportsForSegment={getAirportsForSegment}
                        closeActiveSegmentComponent={closeActiveSegmentComponent}
                        createSegmentClickAwayHandler={createSegmentClickAwayHandler}
                        getMinDateForSegment={getMinDateForSegment}
                    />
                )
            })}

            {/* Add More Button */}
            {canAddMoreSegment && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
                    <Button
                        onClick={onAddMore}
                        variant="outlined"
                        size="medium"
                        startIcon={<AddIcon />}
                        sx={{
                            py: 1,
                            px: 3,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.875rem'
                        }}
                    >
                        {t('addMore')}
                    </Button>
                </Box>
            )}
        </Box>
    )
}

